import React from "react";
import type { AIReview, TextConsultationContext } from "common/models/types";
import type {
  CreateTextConsultationBody,
  CreateTextConsultationData,
  UpdateTextConsultationBody,
} from "api/consultationService";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import TextRecordSlide, { TextRecordPatient } from "components/templates/TextRecordSlide";
import { flushAll as flushAllTextRecordQueues } from "components/templates/TextRecordSlide/textRecordSaveQueue";

interface ApiResult<T> {
  error: number;
  data?: T;
  message?: string;
  status?: number;
}

export interface TextRecordSlideOpenerDeps {
  patientId: number;
  patient: TextRecordPatient | null;
  onCreate: (body: CreateTextConsultationBody) => Promise<ApiResult<CreateTextConsultationData>>;
  onUpdate: (id: number, body: UpdateTextConsultationBody) => Promise<ApiResult<void>>;
  /** 닫힘·상담일 변경 때 목록 재조회(설계 결정 16). */
  afterSave: () => void;
  /** 읽기 화면 「삭제」(확인창 + soft delete). */
  onDelete: (recordId: number) => void;
  /** 저장 실패한 대기 변경을 버리고 읽기로 돌아갈 때 서버 저장본으로 다시 연다(결함 1). */
  reopenFromServer: (recordId: number) => void;
}

// 상담 체크리스트 읽기·수정 슬라이드 고유 key용 모듈 카운터(선례 openDoseReviewTestSlide).
let textRecordOpenSeq = 0;

/**
 * 환자 화면의 상담 체크리스트 읽기·수정 슬라이드 열기(설계 결정 7·20).
 * 둘이 서로를 부르므로(「수정」 ↔ 「닫기」) 한 묶음으로 만든다.
 *
 * 둘 다 진행 중·대기 중인 저장 큐가 빈 뒤에 연다 — 슬라이드마다 큐 key가 새로 생기므로, 읽기 띠 가져오기 →
 * 수정 열기, 수정 닫기 → 읽기 → 수정 순서에서 기다리지 않으면 PATCH 두 개가 동시에 나간다(설계 결정 21 결함 ④).
 */
export function createTextRecordSlideOpeners(deps: TextRecordSlideOpenerDeps) {
  // 읽기 화면("작성됨"·수정 화면 복귀).
  async function showReadSlide(
    recordId: number,
    context: TextConsultationContext,
    review: AIReview | null,
  ): Promise<void> {
    await flushAllTextRecordQueues();
    const key = `text-record:${recordId}:${context.reviewId ?? "none"}:read:${++textRecordOpenSeq}`;
    SlideManager.show({
      body: (
        <TextRecordSlide
          key={key}
          slideKey={key}
          patientId={deps.patientId}
          reviewId={context.reviewId ?? null}
          recordId={recordId}
          review={review}
          initialContext={context}
          mode="read"
          patient={deps.patient}
          onCreate={deps.onCreate}
          onUpdate={deps.onUpdate}
          onClose={deps.afterSave}
          onEdit={(override) => void showEditSlide(recordId, override ?? context, review, true)}
          onDelete={() => deps.onDelete(recordId)}
        />
      ),
      width: "560px",
      noBackdrop: true,
      needCloseButton: false,
    });
  }

  // 수정 화면(설계 결정 7). fromRead면 읽기 → 「수정」으로 연 것이라 「닫기」가 읽기 화면으로 복귀하고,
  // 아니면("작성됨"이 오늘 기록을 바로 연 경우 — 결정 20) 「닫기」가 슬라이드를 닫는다.
  async function showEditSlide(
    recordId: number,
    context: TextConsultationContext,
    review: AIReview | null,
    fromRead: boolean,
  ): Promise<void> {
    await flushAllTextRecordQueues();
    const key = `text-record:${recordId}:${context.reviewId ?? "none"}:edit:${++textRecordOpenSeq}`;
    SlideManager.show({
      body: (
        <TextRecordSlide
          key={key}
          slideKey={key}
          patientId={deps.patientId}
          reviewId={context.reviewId ?? null}
          recordId={recordId}
          review={review}
          initialContext={context}
          mode="edit"
          patient={deps.patient}
          onCreate={deps.onCreate}
          onUpdate={deps.onUpdate}
          onConsultedAtChanged={deps.afterSave}
          onClose={deps.afterSave}
          onReturnToRead={
            fromRead
              ? (cur, discardFailed) =>
                  // 저장 실패한 대기 변경을 버리고 닫으면 서버 저장본으로 읽기 화면을 다시 연다(결함 1).
                  discardFailed ? deps.reopenFromServer(recordId) : void showReadSlide(recordId, cur, review)
              : undefined
          }
        />
      ),
      width: "560px",
      noBackdrop: true,
      needCloseButton: false,
    });
  }

  return { showReadSlide, showEditSlide };
}
